import React, { type JSX } from 'react';
import { Chip, Icon } from '@saviynt/design-system';
import { differenceInDays, isPast } from 'date-fns';

import { P, useInvertedLocalize } from '../../../utilities';
import { type PendingRequest } from '../models';

import { invertedMessages } from './messages';
import RecentAssignmentRow from './RecentAssignmentRow';
import {
  type ConvertToUtc,
  type RecentAssignmentsInvertedLocalize,
} from './types';

import './RequestRow.css';

interface Props {
  req: PendingRequest;
  convertToUtc: ConvertToUtc;
}

/**
 * Wrapper around RecentAssignmentRow for a request.
 */
export default function RequestRow({ req, convertToUtc }: Props): JSX.Element {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <RecentAssignmentRow
      className='Nav_RequestRow'
      icon={<Icon kind='RequestApproval' size='large' color='teal-500' />}
      sourceAndPurpose={
        <div className='Nav_RequestRow-sourceAndPurpose'>
          <P
            variant={2}
            bold
            className='Nav_RequestRow-sourceAndPurposeHeading'>
            {l('Review Request')}
          </P>
          <P variant={2} className='Nav_RequestRow-sourceAndPurposeId'>
            {req.requestid}
          </P>
        </div>
      }
      details={
        <div className='Nav_RequestRow-details'>
          <P variant={3} className='Nav_RequestRow-detailsLabel'>
            {l('Type')}
          </P>
          <P variant={2} className='Nav_RequestRow-detailsValue'>
            {req.requesttype}
          </P>
        </div>
      }
      subject1={
        <div className='Nav_RequestRow-subject1'>
          <P variant={3} className='Nav_RequestRow-subject1Label'>
            {l('Requested by')}
          </P>
          <P variant={2} className='Nav_RequestRow-subject1Value'>
            {req.requestor}
          </P>
        </div>
      }
      subject2={
        <div className='Nav_RequestRow-subject2'>
          <P variant={3} className='Nav_RequestRow-subject2Label'>
            {l('Due')}
          </P>
          <P variant={2} className='Nav_RequestRow-subject2Value'>
            {convertToUtc(req.duedate)}
          </P>
        </div>
      }
      assignmentStatus={assignmentStatus(l, req.duedate)}
    />
  );
}

function assignmentStatus(
  l: RecentAssignmentsInvertedLocalize,
  dueDate: Date
): JSX.Element | undefined {
  const size = 'small';
  const kind = 'subtleFilled';

  if (isPast(dueDate)) {
    return (
      <Chip size={size} kind={kind} color='critical' label={l('Overdue')} />
    );
  }

  const count = differenceInDays(dueDate, new Date());

  return (
    <Chip
      size={size}
      kind={kind}
      color={count <= 1 ? 'critical' : 'brandSecondary'}
      label={l('Due in {count} d', { count })}
    />
  );
}
